// REST client for the Go backend. Holds the device session (tokens) and
// transparently refreshes the access token.
import { nativePlatform, onPhone, testLogin } from "./platform";

export interface XmppInfo {
  jid: string;
  domain: string;
  muc_domain: string;
  ws_url: string;
  resource: string;
}

export interface Profile {
  jid: string;
  phone: string;
  name: string;
  about: string;
  avatar_url: string | null;
  updated_at: number;
  username?: string;
  id?: string;
}

export interface Session {
  access_token: string;
  expires_at: number;
  refresh_token: string;
  session_id: string;
  user: Profile;
  xmpp: XmppInfo;
  /** a companion, linked from the primary phone (not set: logged in by SMS) */
  linked?: boolean;
}

export interface Device {
  id: string;
  device_name: string;
  platform: string;
  ip: string;
  created_at: number;
  last_used_at: number;
  current: boolean;
  linked?: boolean;
  primary?: boolean;
}

export interface NativePushToken {
  transport: "fcm" | "apns";
  token: string;
}

export interface HistoryRequest {
  id: string;
  device_name: string;
  platform: string;
  status: "pending" | "approved" | "declined";
  scope: "recent" | "full"; // recent: the last 30 days (web); full: everything (phone app)
  created_at: number;
  mine?: boolean;
  pub?: string; // requesting device's key (other devices' requests)
  transfer?: { url: string; pub: string; size: number };
}

export interface Uploaded {
  id: string;
  url: string;
  mime: string;
  size: number;
  filename: string;
  width: number | null;
  height: number | null;
}

export type Level = "everyone" | "contacts" | "nobody";

export interface Privacy {
  /** Off: no blue ticks sent in 1:1 chats, and none shown (groups always send). */
  read_receipts: boolean;
  last_seen: Level; // also controls "online"
  profile_photo: Level;
  about: Level;
  groups_add: "everyone" | "contacts";
  /** calls from people you haven't saved don't ring */
  silence_unknown_callers?: boolean;
}

export interface ChatSetting {
  jid: string;
  pinned_at: number | null;
  archived: boolean;
  muted_until: number | null; // unix seconds
}

export interface Star {
  chat: string;
  id: string;
  created_at: number;
}

export interface GroupMember {
  jid: string;
  role: "owner" | "admin" | "member";
  joined_at: number;
}

export interface Group {
  jid: string;
  name: string;
  description: string;
  avatar_url: string | null;
  only_admins_send: boolean;
  admins_send_since: number | null;
  only_admins_edit: boolean;
  share_history?: boolean; // new members get the recent messages
  approve_joins?: boolean; // invite-link joins need an admin's approval
  created_at: number;
  updated_at: number;
  members: GroupMember[];
  my_role: "owner" | "admin" | "member" | "";
  invite_code?: string;
  ttl?: number; // disappearing messages timer (seconds, 0 = off)
  community?: string | null; // community id when linked
  skipped?: string[]; // members not added because of their privacy settings
  /** client-side: we are no longer a participant */
  left?: boolean;
}

// ---- phase 7 --------------------------------------------------------------------

export interface StatusItem {
  id: string;
  owner: string;
  kind: "text" | "image" | "video";
  envelope: unknown; // end-to-end encrypted content (our key entries only)
  media_url?: string;
  created_at: number; // ms
  expires_at: number;
  viewed?: boolean;
  reaction?: string; // ours (feed)
  views?: { jid: string; at: number; reaction?: string }[];
}

export interface StatusAudience {
  audience: string[];
  privacy: { audience: "contacts" | "except" | "only"; list: string[] };
}

export interface Channel {
  jid: string;
  name: string;
  description: string;
  avatar_url: string | null;
  followers: number;
  my_role: "owner" | "admin" | "follower" | "";
  created_at: number;
  updated_at: number;
}

export interface ChannelPostContent {
  body: string;
  kind: string;
  media?: { id?: string; mime: string; size: number; name?: string; width?: number; height?: number; duration?: number; thumb?: string; waveform?: number[] };
  preview?: { url: string; title: string; description?: string; site_name?: string; thumb?: string };
}

export interface ChannelPost {
  id: string;
  created_at: number;
  edited_at?: number;
  deleted: boolean;
  content?: ChannelPostContent;
  media_url?: string;
  reactions?: Record<string, number>;
  my_reaction?: string;
}

export interface Broadcast {
  id: string;
  name: string;
  members: { jid: string; receives: boolean }[];
  created_at: number;
}

export interface Community {
  id: string;
  name: string;
  description: string;
  avatar_url: string | null;
  announce: string;
  members: number;
  my_role: "admin" | "member" | "";
  groups: { jid: string; name: string; avatar_url: string | null; members: number; joined: boolean }[];
  created_at: number;
}

export interface InvitePreview {
  jid: string;
  name: string;
  description: string;
  avatar_url: string | null;
  member_count: number;
  is_member: boolean;
  approve_joins?: boolean; // an admin has to approve
  requested?: boolean; // you asked already
}

export interface LinkTransfer {
  url: string;
  pub: string; // approving device's ephemeral ECDH public key
  size: number;
}

export interface BackupInfo {
  url: string;
  salt: string;
  iterations: number;
  messages: number;
  size: number;
  created_at: number;
}

export class ApiError extends Error {
  constructor(public status: number, public code: string, message: string) {
    super(message);
  }
}

// Several accounts can be logged in on one device (like WhatsApp's "Add
// account"); one is active at a time. Each keeps its own device session,
// chat store and keys (per-account IndexedDB).
const KEY = "xmm.session"; // the active account
const ACCOUNTS = "xmm.accounts"; // every account logged in here
export const MAX_ACCOUNTS = 5;

let accounts: Session[] = loadAccounts();
let session: Session | null = load();
let refreshing: Promise<void> | null = null;
const listeners = new Set<(s: Session | null) => void>();

function load(): Session | null {
  try {
    const raw = localStorage.getItem(KEY);
    const s = raw ? (JSON.parse(raw) as Session) : null;
    if (s && !accounts.some((a) => a.xmpp.jid === s.xmpp.jid)) accounts = [...accounts, s];
    return s ?? accounts[0] ?? null;
  } catch {
    return null;
  }
}

function loadAccounts(): Session[] {
  try {
    return JSON.parse(localStorage.getItem(ACCOUNTS) ?? "[]") as Session[];
  } catch {
    return [];
  }
}

function persist() {
  try {
    if (session) localStorage.setItem(KEY, JSON.stringify(session));
    else localStorage.removeItem(KEY);
    localStorage.setItem(ACCOUNTS, JSON.stringify(accounts));
  } catch {
    /* storage unavailable: sessions live in memory only */
  }
}

/** Make s the active account (adding or updating it); null drops the active
 * account and switches to the next one, if any. */
function save(s: Session | null) {
  if (s) {
    const i = accounts.findIndex((a) => a.xmpp.jid === s.xmpp.jid);
    accounts = i < 0 ? [...accounts, s] : accounts.map((a, j) => (j === i ? s : a));
  } else if (session) {
    const gone = session.xmpp.jid;
    accounts = accounts.filter((a) => a.xmpp.jid !== gone);
  }
  session = s ?? accounts[0] ?? null;
  persist();
  listeners.forEach((l) => l(session));
}

/** Every account logged in on this device (the active one included). */
export const getAccounts = () => accounts;
/** Switch to another account logged in on this device. */
export function switchAccount(jid: string) {
  const s = accounts.find((a) => a.xmpp.jid === jid);
  if (s && s.xmpp.jid !== session?.xmpp.jid) save(s);
}

export const getSession = () => session;
export const onSession = (fn: (s: Session | null) => void) => {
  listeners.add(fn);
  return () => listeners.delete(fn);
};

async function raw<T>(method: string, path: string, body?: unknown, token?: string): Promise<T> {
  const headers: Record<string, string> = {};
  let payload: BodyInit | undefined;
  if (body instanceof FormData) payload = body;
  else if (body !== undefined) {
    headers["Content-Type"] = "application/json";
    payload = JSON.stringify(body);
  }
  // Not "Authorization": that header may be taken by a basic-auth proxy.
  if (token) headers["X-Xmm-Token"] = token;
  if (testLogin()) headers["X-Xmm-Test-Login"] = "1";
  const res = await fetch(path, { method, headers, body: payload });
  if (res.status === 204) return undefined as T;
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const e = data?.error ?? {};
    throw new ApiError(res.status, e.code ?? "http_" + res.status, e.message ?? res.statusText);
  }
  return data as T;
}

async function refresh(): Promise<void> {
  if (!session) throw new ApiError(401, "no_session", "not logged in");
  const cur = session;
  refreshing ??= (async () => {
    try {
      const s = await raw<Session>("POST", "/api/v1/auth/refresh", { refresh_token: cur.refresh_token });
      // Switched accounts meanwhile: just keep the new tokens for this one.
      if (session?.xmpp.jid === cur.xmpp.jid) save(s);
      else {
        accounts = accounts.map((a) => (a.xmpp.jid === s.xmpp.jid ? s : a));
        persist();
      }
    } catch (e) {
      if (e instanceof ApiError && e.status === 401 && session?.xmpp.jid === cur.xmpp.jid) save(null);
      throw e;
    } finally {
      refreshing = null;
    }
  })();
  return refreshing;
}

/** A valid access token, refreshed if it expires within a minute. */
export async function accessToken(): Promise<string> {
  if (!session) throw new ApiError(401, "no_session", "not logged in");
  if (session.expires_at * 1000 - Date.now() < 60_000) await refresh();
  return session!.access_token;
}

async function authed<T>(method: string, path: string, body?: unknown): Promise<T> {
  try {
    return await raw<T>(method, path, body, await accessToken());
  } catch (e) {
    if (e instanceof ApiError && e.status === 401 && session) {
      await refresh();
      return raw<T>(method, path, body, session!.access_token);
    }
    throw e;
  }
}

export const api = {
  config: () => raw<{ media_base: string; instance: string; push: { web: boolean; fcm: boolean; apns: boolean } }>("GET", "/api/v1/config"),
  requestOtp: (phone: string) =>
    raw<{ phone: string; expires_in: number; resend_in: number; dev_code?: string }>("POST", "/api/v1/auth/otp/request", { phone, platform: nativePlatform }),

  /** Returns the session, or a PIN challenge when two-step verification is on. */
  async verifyOtp(phone: string, code: string): Promise<(Session & { new_user: boolean }) | { pin_required: true; pin_token: string }> {
    const s = await raw<(Session & { new_user: boolean }) | { pin_required: true; pin_token: string }>("POST", "/api/v1/auth/otp/verify", {
      phone,
      code,
      device_name: deviceName(),
      platform: nativePlatform,
    });
    if (!("pin_required" in s)) save(s);
    return s;
  },

  async verifyPin(pin_token: string, pin: string) {
    const s = await raw<Session & { new_user: boolean }>("POST", "/api/v1/auth/pin", { pin_token, pin });
    save(s);
    return s;
  },
  pinStatus: () => authed<{ enabled: boolean }>("GET", "/api/v1/me/pin"),
  setPin: (pin: string, current_pin?: string) => authed<{ enabled: boolean }>("PUT", "/api/v1/me/pin", { pin, current_pin: current_pin ?? "" }),
  removePin: (current_pin: string) => authed<{ enabled: boolean }>("DELETE", "/api/v1/me/pin", { current_pin }),

  pushVapid: () => authed<{ public_key: string; jid: string }>("GET", "/api/v1/push/vapid"),
  /** A Web Push subscription, or a native app's FCM/APNs device token. */
  putPush: (sub: PushSubscriptionJSON | NativePushToken) =>
    authed<{ jid: string; node: string; secret: string }>("PUT", "/api/v1/push", "transport" in sub ? sub : { endpoint: sub.endpoint, keys: sub.keys }),
  deletePush: () => authed<void>("DELETE", "/api/v1/push"),

  timers: () => authed<{ timers: { jid: string; ttl: number; set_at: number }[] }>("GET", "/api/v1/timers"),
  setTimer: (jid: string, ttl: number) => authed<{ jid: string; ttl: number }>("PUT", "/api/v1/timers/" + encodeURIComponent(jid), { ttl }),

  async logout() {
    try {
      await authed("POST", "/api/v1/auth/logout");
    } finally {
      save(null);
    }
  },

  /** Drop the local session without calling the server (e.g. revoked). */
  forget: () => save(null),
  /** Use a session obtained another way (QR device linking). */
  adopt: (s: Session) => save(s),

  // QR device linking: new device side (no session yet) ...
  // (With a phone number and public key: linking by code instead of a QR.)
  createLink: (byCode?: { phone: string; pub: string }) =>
    raw<{ id: string; token: string; expires_in: number }>("POST", "/api/v1/link/requests", { device_name: deviceName(), platform: nativePlatform, ...byCode }),
  pollLink: (id: string, token: string) =>
    raw<{ status: "pending" } | { status: "approved"; session: Session; transfer: LinkTransfer | null }>("GET", `/api/v1/link/requests/${id}/poll?token=${encodeURIComponent(token)}`),
  // ... and the approving (logged-in) device
  linkInfo: (id: string) => authed<{ device_name: string; platform: string }>("GET", "/api/v1/link/requests/" + id),
  linkByCode: (code: string) => authed<{ id: string; pub: string; device_name: string; platform: string }>("GET", "/api/v1/link/code/" + encodeURIComponent(code)),
  approveLink: (id: string, transfer: LinkTransfer | null) => authed<void>("POST", `/api/v1/link/requests/${id}/approve`, { transfer }),

  backup: () => authed<{ backup: BackupInfo | null }>("GET", "/api/v1/backup"),
  putBackup: (b: { media_id: string; salt: string; iterations: number; messages: number }) => authed<{ backup: BackupInfo }>("PUT", "/api/v1/backup", b),
  deleteBackup: () => authed<void>("DELETE", "/api/v1/backup"),

  // status
  statusAudience: () => authed<StatusAudience>("GET", "/api/v1/status/audience"),
  putStatusPrivacy: (audience: "contacts" | "except" | "only", list: string[]) => authed<StatusAudience>("PUT", "/api/v1/status/privacy", { audience, list }),
  postStatus: (s: { id: string; kind: string; envelope: unknown; media_id?: string; audience: string[] }) => authed<{ id: string; audience: number }>("POST", "/api/v1/status", s),
  statusFeed: () => authed<{ statuses: StatusItem[] }>("GET", "/api/v1/status/feed"),
  myStatuses: () => authed<{ statuses: StatusItem[] }>("GET", "/api/v1/status/mine"),
  viewStatus: (id: string) => authed<void>("POST", `/api/v1/status/${encodeURIComponent(id)}/view`),
  reactStatus: (id: string, emoji: string) => authed<void>("POST", `/api/v1/status/${encodeURIComponent(id)}/react`, { emoji }),
  deleteStatus: (id: string) => authed<void>("DELETE", `/api/v1/status/${encodeURIComponent(id)}`),

  // channels
  channels: () => authed<{ channels: Channel[] }>("GET", "/api/v1/channels"),
  channelDirectory: (q: string) => authed<{ channels: Channel[] }>("GET", "/api/v1/channels/directory?q=" + encodeURIComponent(q)),
  createChannel: (c: { name: string; description?: string; avatar_media_id?: string }) => authed<Channel>("POST", "/api/v1/channels", c),
  channel: (jid: string) => authed<Channel>("GET", "/api/v1/channels/" + encodeURIComponent(jid)),
  patchChannel: (jid: string, p: { name?: string; description?: string; avatar_media_id?: string }) => authed<Channel>("PATCH", "/api/v1/channels/" + encodeURIComponent(jid), p),
  deleteChannel: (jid: string) => authed<void>("DELETE", "/api/v1/channels/" + encodeURIComponent(jid)),
  follow: (jid: string, on: boolean) => authed<Channel>(on ? "POST" : "DELETE", `/api/v1/channels/${encodeURIComponent(jid)}/follow`),
  channelPosts: (jid: string, before?: number) => authed<{ posts: ChannelPost[] }>("GET", `/api/v1/channels/${encodeURIComponent(jid)}/posts${before ? "?before=" + before : ""}`),
  createPost: (jid: string, p: ChannelPostContent & { id: string }) => authed<ChannelPost>("POST", `/api/v1/channels/${encodeURIComponent(jid)}/posts`, p),
  editPost: (jid: string, id: string, body: string) => authed<void>("PATCH", `/api/v1/channels/${encodeURIComponent(jid)}/posts/${encodeURIComponent(id)}`, { body }),
  deletePost: (jid: string, id: string) => authed<void>("DELETE", `/api/v1/channels/${encodeURIComponent(jid)}/posts/${encodeURIComponent(id)}`),
  reactPost: (jid: string, id: string, emoji: string | null) =>
    authed<{ reactions: Record<string, number>; my_reaction?: string }>("PUT", `/api/v1/channels/${encodeURIComponent(jid)}/posts/${encodeURIComponent(id)}/reaction`, { emoji }),
  postReactions: (jid: string, ids: string[]) =>
    authed<{ reactions: Record<string, { reactions: Record<string, number>; my_reaction: string }> }>("GET", `/api/v1/channels/${encodeURIComponent(jid)}/reactions?ids=${ids.map(encodeURIComponent).join(",")}`),

  // broadcast lists
  broadcasts: () => authed<{ broadcasts: Broadcast[] }>("GET", "/api/v1/broadcasts"),
  putBroadcast: (id: string, name: string, members: string[]) => authed<{ broadcasts: Broadcast[] }>("PUT", "/api/v1/broadcasts/" + encodeURIComponent(id), { name, members }),
  deleteBroadcast: (id: string) => authed<void>("DELETE", "/api/v1/broadcasts/" + encodeURIComponent(id)),

  // communities
  communities: () => authed<{ communities: Community[] }>("GET", "/api/v1/communities"),
  createCommunity: (c: { name: string; description?: string; avatar_media_id?: string; groups: string[] }) => authed<Community>("POST", "/api/v1/communities", c),
  patchCommunity: (id: string, p: { name?: string; description?: string; avatar_media_id?: string }) => authed<Community>("PATCH", "/api/v1/communities/" + encodeURIComponent(id), p),
  addCommunityGroup: (id: string, g: { group?: string; name?: string }) => authed<Community>("POST", `/api/v1/communities/${encodeURIComponent(id)}/groups`, g),
  removeCommunityGroup: (id: string, group: string) => authed<Community>("DELETE", `/api/v1/communities/${encodeURIComponent(id)}/groups/${encodeURIComponent(group)}`),
  joinCommunityGroup: (id: string, group: string) => authed<Group>("POST", `/api/v1/communities/${encodeURIComponent(id)}/groups/${encodeURIComponent(group)}/join`),
  leaveCommunity: (id: string) => authed<void>("POST", `/api/v1/communities/${encodeURIComponent(id)}/leave`),

  keyCount: () => authed<{ device_id: number; prekeys: number }>("GET", "/api/v1/keys/count"),
  putKeys: (b: { device_id: number; identity_key: string; signed_prekey: { id: number; key: string; sig: string }; prekeys: { id: number; key: string }[] }) =>
    authed<{ device_id: number; prekeys: number }>("PUT", "/api/v1/keys", b),
  keyDevices: (jids: string[]) =>
    authed<{ devices: { jid: string; device_id: number; identity_key: string; since?: number }[] }>("GET", "/api/v1/keys/devices?" + jids.map((j) => "jid=" + encodeURIComponent(j)).join("&")),
  keyBundles: (devices: { jid: string; device_id: number }[]) =>
    authed<{
      bundles: {
        jid: string;
        device_id: number;
        identity_key: string;
        signed_prekey: { id: number; key: string; sig: string };
        prekey?: { id: number; key: string };
      }[];
    }>("POST", "/api/v1/keys/bundles", { devices }),

  privacy: () => authed<Privacy>("GET", "/api/v1/me/privacy"),
  patchPrivacy: (p: Partial<Privacy>) => authed<Privacy>("PATCH", "/api/v1/me/privacy", p),

  /** Short-lived XMPP login token; fails with 401 once the device is removed. */
  xmppToken: () => authed<{ token: string; expires_at: number; xmpp: XmppInfo }>("GET", "/api/v1/auth/xmpp-token"),

  me: () => authed<Profile>("GET", "/api/v1/me"),
  async updateMe(p: { name?: string; about?: string; avatar_media_id?: string; username?: string }) {
    const u = await authed<Profile>("PATCH", "/api/v1/me", p);
    if (session) save({ ...session, user: u });
    return u;
  },

  users: (jids: string[]) =>
    authed<{ users: Profile[] }>("GET", "/api/v1/users?" + jids.map((j) => "jid=" + encodeURIComponent(j)).join("&")),
  lookup: (phones: string[]) => authed<{ users: Profile[] }>("POST", "/api/v1/contacts/lookup", { phones }),
  lookupContacts: (phones: string[]) => authed<{ users: Profile[] }>("POST", "/api/v1/contacts/lookup", { phones }),
  addContact: (phone: string, name: string) =>
    authed<{ user: Profile; name: string }>("POST", "/api/v1/contacts", phone.startsWith("@") ? { username: phone.slice(1), name } : { phone, name }),
  userByUsername: (name: string) => authed<{ user: Profile }>("GET", "/api/v1/users/by-username/" + encodeURIComponent(name.replace(/^@/, ""))),
  /** Private contact discovery: 16-bit buckets of number hashes in, full hashes out. */
  discoverContacts: (buckets: number[]) => authed<{ hashes: string[] }>("POST", "/api/v1/contacts/discover", { buckets }),
  removeContact: (jid: string) => authed<void>("DELETE", "/api/v1/contacts/" + encodeURIComponent(jid)),

  devices: () => authed<{ devices: Device[] }>("GET", "/api/v1/devices"),
  removeDevice: (id: string) => authed<void>("DELETE", "/api/v1/devices/" + id),
  historyRequests: () => authed<{ requests: HistoryRequest[]; first: boolean; others: number; scope: "recent" | "full"; primary?: boolean }>("GET", "/api/v1/history/requests"),
  historyBackup: () =>
    authed<{ key: string; version: number; backup: { url: string; messages: number; size: number; updated_at: number } | null }>("GET", "/api/v1/history/backup"),
  gifs: (q: string, pos = "") =>
    authed<{ gifs: { id: string; title?: string; preview: string; url: string; width: number; height: number }[]; next: string }>(
      "GET",
      `/api/v1/gifs?q=${encodeURIComponent(q)}&pos=${encodeURIComponent(pos)}`,
    ),
  stickerStore: (q: string, page: number) =>
    authed<{ packs: { id: string; key: string; title: string; author: string; cover: number; animated?: boolean; featured?: boolean }[]; more: boolean; total: number }>(
      "GET",
      `/api/v1/stickers/store?q=${encodeURIComponent(q)}&page=${page}`,
    ),
  stickerPack: (id: string, key: string) =>
    authed<{ id: string; key: string; title: string; author: string; cover: number; stickers: { id: number; emoji?: string }[] }>("GET", `/api/v1/stickers/packs/${id}?key=${key}`),
  putHistoryBackup: (b: { media_id: string; messages: number; base: number }) => authed<{ version: number }>("PUT", "/api/v1/history/backup", b),
  requestHistory: (pub: string) => authed<{ id: string; scope: "recent" | "full" }>("POST", "/api/v1/history/requests", { pub }),
  approveHistory: (id: string, t: { url: string; pub: string; size: number }) => authed<void>("POST", `/api/v1/history/requests/${id}/approve`, t),

  upload(file: Blob, filename = "file") {
    const fd = new FormData();
    fd.append("file", file, filename);
    return authed<Uploaded>("POST", "/api/v1/media", fd);
  },

  /** Upload with progress (fetch cannot report upload progress). */
  async uploadWithProgress(file: Blob, filename: string, onProgress: (fraction: number) => void, signal?: AbortSignal): Promise<Uploaded> {
    const token = await accessToken();
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open("POST", "/api/v1/media");
      xhr.setRequestHeader("X-Xmm-Token", token);
      xhr.upload.onprogress = (e) => e.lengthComputable && onProgress(e.loaded / e.total);
      xhr.onload = () => {
        let data: any = {};
        try {
          data = JSON.parse(xhr.responseText);
        } catch {
          /* non-JSON error page */
        }
        if (xhr.status >= 200 && xhr.status < 300) resolve(data as Uploaded);
        else reject(new ApiError(xhr.status, data?.error?.code ?? "upload_failed", data?.error?.message ?? "upload failed"));
      };
      xhr.onerror = () => reject(new ApiError(0, "network", "network error during upload"));
      xhr.onabort = () => reject(new ApiError(0, "aborted", "upload cancelled"));
      signal?.addEventListener("abort", () => xhr.abort());
      const fd = new FormData();
      fd.append("file", file, filename);
      xhr.send(fd);
    });
  },

  linkPreview: (url: string) =>
    authed<{ url: string; title: string; description?: string; site_name?: string; thumb?: string }>("GET", "/api/v1/link-preview?url=" + encodeURIComponent(url)),

  chatSettings: () => authed<{ chats: ChatSetting[] }>("GET", "/api/v1/chat-settings"),
  patchChat: (jid: string, p: { pinned?: boolean; archived?: boolean; muted_until?: number }) =>
    authed<void>("PATCH", "/api/v1/chat-settings/" + encodeURIComponent(jid), p),

  groups: () => authed<{ groups: Group[] }>("GET", "/api/v1/groups"),
  group: (jid: string) => authed<Group>("GET", "/api/v1/groups/" + encodeURIComponent(jid)),
  createGroup: (g: { name: string; members: string[]; description?: string; avatar_media_id?: string }) => authed<Group>("POST", "/api/v1/groups", g),
  patchGroup: (jid: string, p: { name?: string; description?: string; avatar_media_id?: string; only_admins_send?: boolean; only_admins_edit?: boolean; share_history?: boolean; approve_joins?: boolean }) =>
    authed<Group>("PATCH", "/api/v1/groups/" + encodeURIComponent(jid), p),
  addGroupMembers: (jid: string, members: string[]) => authed<Group>("POST", `/api/v1/groups/${encodeURIComponent(jid)}/members`, { members }),
  removeGroupMember: (jid: string, member: string) => authed<void>("DELETE", `/api/v1/groups/${encodeURIComponent(jid)}/members/${encodeURIComponent(member)}`),
  setGroupRole: (jid: string, member: string, role: "admin" | "member") =>
    authed<Group>("PUT", `/api/v1/groups/${encodeURIComponent(jid)}/members/${encodeURIComponent(member)}/role`, { role }),
  resetInvite: (jid: string) => authed<Group>("POST", `/api/v1/groups/${encodeURIComponent(jid)}/invite`),
  revokeInvite: (jid: string) => authed<Group>("DELETE", `/api/v1/groups/${encodeURIComponent(jid)}/invite`),
  invite: (code: string) => authed<InvitePreview>("GET", "/api/v1/invites/" + encodeURIComponent(code)),
  /** A Group, or (groups that approve new participants) "requested". */
  joinInvite: (code: string) => authed<Group | { requested: true; jid: string; name: string }>("POST", `/api/v1/invites/${encodeURIComponent(code)}/join`),
  joinRequests: (jid: string) => authed<{ requests: { jid: string; name: string; requested_at: number }[] }>("GET", `/api/v1/groups/${encodeURIComponent(jid)}/requests`),
  answerJoinRequest: (jid: string, user: string, approve: boolean) =>
    authed<void>("POST", `/api/v1/groups/${encodeURIComponent(jid)}/requests/${encodeURIComponent(user)}/${approve ? "approve" : "decline"}`),
  report: (r: { jid: string; reason: string; messages?: { from: string; ts: number; text: string }[] }) => authed<void>("POST", "/api/v1/reports", r),

  stars: () => authed<{ stars: Star[] }>("GET", "/api/v1/stars"),
  star: (chat: string, id: string, on: boolean) =>
    authed<void>(on ? "PUT" : "DELETE", `/api/v1/stars/${encodeURIComponent(chat)}/${encodeURIComponent(id)}`),
};

function deviceName(): string {
  const ua = navigator.userAgent;
  const app = /xmm-app\/[\d.]+ \((Android|iOS)/.exec(ua);
  if (app) return `xmm app on ${app[1]}`;
  const browser = /Edg\//.test(ua) ? "Edge" : /Firefox\//.test(ua) ? "Firefox" : /Chrome\//.test(ua) ? "Chrome" : /Safari\//.test(ua) ? "Safari" : "Browser";
  const os = /Windows/.test(ua) ? "Windows" : /Mac OS X/.test(ua) ? "macOS" : /Android/.test(ua) ? "Android" : /iPhone|iPad/.test(ua) ? "iOS" : /Linux/.test(ua) ? "Linux" : "";
  return os ? `${browser} on ${os}` : browser;
}

/** The account's main phone: a phone that logged in with an SMS code (a
 * phone linked from it is a companion, like a browser). It links and logs
 * out the other devices and syncs the address book. */
export const isPrimary = () => onPhone && !!getSession() && !getSession()!.linked;
